import type { ApiError } from '../types';
import { getPassword, clearPassword } from '../utils/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class ApiClient {
  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const password = getPassword();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers as Record<string, string>,
    };

    // Add password header if available
    if (password) {
      headers['X-Viewer-Password'] = password;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401) {
        // Clear invalid password
        clearPassword();
        // Reload page to show auth prompt
        window.location.reload();
        throw new AuthenticationError('認証が必要です。ページを再読み込みしてパスワードを入力してください。');
      }

      try {
        const error: ApiError = await response.json();
        throw new Error(error.error.message);
      } catch (e) {
        if (e instanceof AuthenticationError) {
          throw e;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
